import React from 'react';
import { Button, Typography, Space, Row, Col } from 'antd';
import { PhoneOutlined, CarOutlined, CheckCircleOutlined, ToolOutlined, AppstoreOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { shopConfig, getThemeColors, getThemeGradient } from '../../config/shop.config';

const { Title, Text } = Typography;

const HeroSection: React.FC = () => {
  const themeColors = getThemeColors();
  const themeGradient = getThemeGradient();
  const navigate = useNavigate();

  const handleCallClick = () => {
    window.location.href = `tel:${shopConfig.phone}`;
  };

  const heroBackground = shopConfig.branding?.heroBackground;
  const logo = shopConfig.branding?.logo;

  const backgroundStyle = heroBackground
    ? {
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.65)), url(${heroBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }
    : {
        background: themeGradient,
      };

  return (
    <div
      style={{
        ...backgroundStyle,
        padding: window.innerWidth < 768 ? '60px 20px' : '100px 20px',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Decorative Elements - Automotive Theme */}
      <div
        style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '300px',
          height: '300px',
          background: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '50%',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-100px',
          left: '-100px',
          width: '400px',
          height: '400px',
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '50%',
          pointerEvents: 'none',
        }}
      />

      <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <Row gutter={[48, 48]} align="middle">
          <Col xs={24} lg={14}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              {/* Logo */}
              {logo && (
                <div
                  style={{
                    marginBottom: '20px',
                    padding: '16px 24px',
                    background: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '12px',
                    display: 'inline-block',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                  }}
                >
                  <img
                    src={logo}
                    alt={shopConfig.name}
                    style={{
                      maxWidth: window.innerWidth < 768 ? '180px' : '250px',
                      maxHeight: '100px',
                      objectFit: 'contain',
                      display: 'block',
                    }}
                  />
                </div>
              )}

              {/* Shop Name */}
              <Title
                level={1}
                style={{
                  color: 'white',
                  marginBottom: 0,
                  fontSize: window.innerWidth < 768 ? '36px' : '56px',
                  fontWeight: 800,
                  textShadow: '2px 2px 8px rgba(0, 0, 0, 0.3)',
                  lineHeight: 1.2,
                }}
              >
                {shopConfig.name}
              </Title>

              {/* Tagline */}
              <Title
                level={3}
                style={{
                  color: 'rgba(255, 255, 255, 0.95)',
                  fontWeight: 500,
                  fontSize: window.innerWidth < 768 ? '18px' : '24px',
                  marginTop: 0,
                  textShadow: '1px 1px 4px rgba(0, 0, 0, 0.3)',
                }}
              >
                {shopConfig.tagline}
              </Title>

              {/* Key Features */}
              <div style={{ marginTop: '20px' }}>
                {[
                  'Dealer-Level Diagnostics & Repairs',
                  'All Makes & Models - Domestic & Import',
                  'ASE Certified Technicians',
                ].map((feature, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      marginBottom: '12px',
                    }}
                  >
                    <CheckCircleOutlined
                      style={{
                        fontSize: '20px',
                        color: themeColors.accent || '#10b981',
                      }}
                    />
                    <Text
                      style={{
                        fontSize: window.innerWidth < 768 ? '15px' : '17px',
                        color: 'rgba(255, 255, 255, 0.95)',
                        fontWeight: 500,
                      }}
                    >
                      {feature}
                    </Text>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div style={{ marginTop: '30px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                <Button
                  type="primary"
                  size="large"
                  icon={<PhoneOutlined />}
                  onClick={handleCallClick}
                  style={{
                    height: '60px',
                    fontSize: window.innerWidth < 768 ? '16px' : '20px',
                    fontWeight: 700,
                    padding: '0 40px',
                    backgroundColor: themeColors.accent || '#10b981',
                    borderColor: themeColors.accent || '#10b981',
                    borderRadius: '8px',
                    boxShadow: `0 10px 30px ${themeColors.accent || '#10b981'}40`,
                    border: 'none',
                  }}
                >
                  Call Now: {shopConfig.cta.phoneDisplay}
                </Button>
                <Button
                  size="large"
                  icon={<AppstoreOutlined />}
                  onClick={() => navigate('/services')}
                  style={{
                    height: '60px',
                    fontSize: window.innerWidth < 768 ? '16px' : '20px',
                    fontWeight: 700,
                    padding: '0 40px',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    color: themeColors.primary,
                    borderColor: 'white',
                    borderRadius: '8px',
                    boxShadow: '0 10px 30px rgba(255, 255, 255, 0.3)',
                    border: 'none',
                  }}
                >
                  Browse Services
                </Button>
              </div>
            </Space>
          </Col>

          {/* Right Side - Visual Element */}
          <Col xs={24} lg={10}>
            <div
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(20px)',
                borderRadius: '20px',
                padding: '40px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                textAlign: 'center',
              }}
            >
              <CarOutlined style={{ fontSize: '80px', color: 'white', marginBottom: '20px' }} />
              <Title level={4} style={{ color: 'white', marginBottom: '16px' }}>
                Expert Automotive Care
              </Title>
              <Text style={{ fontSize: '16px', color: 'rgba(255, 255, 255, 0.9)', display: 'block' }}>
                From engine swaps to oil changes, we provide comprehensive automotive services with
                dealer-level quality at fair prices.
              </Text>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default HeroSection;
